/**
 * Generic hook for managing list page state.
 * Provides state management for filters, pagination, selection, modals, and advanced filters.
 * Holds filterConditionGroups as the single source of truth; flat filterConditions is derived.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { Selection, SortDescriptor } from 'react-aria-components';

import type { FilterCondition } from '@/lib/crm/filters/types';
import type { FilterConditionGroup } from '@/lib/crm/filters/types';
import type { InitialListUrlState } from '@/lib/crm/list/urlListParams';
import type { CrmObjectFilters } from '@/types/crm';
import {
  convertConditionGroupsToFilterGroups,
  filterGroupsToConditionGroups,
  mergeWithDefaultFilters,
} from '@/utils/filterUtils';

const MAX_EXCLUSIONS = 100;
const EMPTY_FILTER_CONDITION_GROUPS: FilterConditionGroup[] = [];

export interface ListState {
  // Tab state
  activeTab: string;
  setActiveTab: (tab: string) => void;

  // Selection state
  selectedKeys: 'all' | Set<string | number>;
  setSelectedKeys: (keys: 'all' | Set<string | number>) => void;
  excludedIds: Set<string | number>;
  setExcludedIds: (ids: Set<string | number>) => void;
  handleSelectionChange: (newSelection: Selection) => void;

  // Filter state
  filters: CrmObjectFilters;
  setFilters: (filters: CrmObjectFilters) => void;

  // Modal state
  isCreateModalOpen: boolean;
  setIsCreateModalOpen: (open: boolean) => void;
  showAdvancedFilters: boolean;
  setShowAdvancedFilters: (show: boolean) => void;

  // Advanced filter state — group-native (authoritative)
  filterConditionGroups: FilterConditionGroup[];
  appliedFilterConditionGroups: FilterConditionGroup[];
  setFilterConditionGroups: (groups: FilterConditionGroup[]) => void;
  /** Derived: flat conditions across all groups (back-compat). */
  filterConditions: FilterCondition[];
  /** Shim: wraps conditions into a single group then calls setFilterConditionGroups. */
  setFilterConditions: (conditions: FilterCondition[]) => void;
  /**
   * Snapshot of the conditions that were last applied via
   * `handleApplyAdvancedFilters`. Use this (not `filterConditions`) to drive
   * anything that should reflect "what's currently affecting the result set",
   * like the filter-count badge or the empty-state variant — `filterConditions`
   * updates on every keystroke while the slideout is open.
   *
   * Derived: flat conditions across appliedFilterConditionGroups (back-compat).
   */
  appliedFilterConditions: FilterCondition[];

  // Sort state
  sortDescriptor: SortDescriptor | null;
  handleSortChange: (descriptor: SortDescriptor | null) => void;

  // Filter handlers
  handleSearch: (query: string) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleFilterChange: (key: string, value: string | undefined) => void;
  handleApplyAdvancedFilters: (groups?: FilterConditionGroup[]) => void;
  handleResetFilters: () => void;
}

export function isAllSelected(
  selectedKeys: ListState['selectedKeys']
): boolean {
  return selectedKeys === 'all';
}

export function isAllExceptMode(
  selectedKeys: ListState['selectedKeys'],
  excludedIds: Set<string | number>
): boolean {
  return selectedKeys === 'all' && excludedIds.size > 0;
}

export function getExcludedCount(excludedIds: Set<string | number>): number {
  return excludedIds.size;
}

export function getEffectiveCount(
  selectedKeys: ListState['selectedKeys'],
  excludedIds: Set<string | number>,
  totalFilteredCount: number
): number {
  if (selectedKeys === 'all') {
    return totalFilteredCount - excludedIds.size;
  }
  return selectedKeys.size;
}

/**
 * Hook for managing list page state.
 *
 * @param defaultFilters - Default filters to initialize with
 * @param defaultTab - Default tab to select on load (default: 'overview')
 * @returns List state and handlers
 *
 * @example
 * ```tsx
 * const listState = useListState({
 *   search: '',
 *   sort: 'updated_at',
 *   order: 'desc',
 *   page: 1,
 *   page_size: 20,
 * }, 'table');
 * ```
 */
export function useListState(
  defaultFilters: Partial<CrmObjectFilters> = {},
  defaultTab: string = 'overview',
  options?: {
    onExclusionLimitReached?: () => void;
    currentPageIds?: (string | number)[];
    /**
     * Conditions to seed as visible, removable filter chips on first load.
     *
     * Use this (instead of `defaultFilters.filter_groups`) when defaults
     * should appear in the filter bar and be user-clearable. The page-level
     * `mergeWithDefaultFilters` auto-merge is suppressed in this mode so
     * that clicking the chip's X actually removes the filter — without it,
     * the merge re-injects the defaults on every apply.
     *
     * `defaultFilters.filter_groups` (the legacy path) remains the way to
     * express genuinely-forced filters that aren't user-removable, e.g.
     * org-scoped permission filters.
     */
    defaultFilterConditions?: FilterCondition[];
    /**
     * Frozen mount-time snapshot of the URL-synced list params (from
     * `readInitialListUrlState`). When `filterGroups` is non-null (INCLUDING
     * `[]`, which means "user explicitly cleared the default chips"), it is
     * the complete user filter set and takes precedence over
     * `defaultFilterConditions` for seeding — the first query then already
     * runs with the URL's filters, so a deep link never flashes unfiltered
     * results. `null` fields fall back to today's default seeding.
     */
    initialUrlState?: InitialListUrlState;
  }
): ListState {
  const defaultFilterConditions = options?.defaultFilterConditions;
  const initialUrlState = options?.initialUrlState;

  // Tab state
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Selection state
  const [selectedKeys, setSelectedKeys] = useState<
    'all' | Set<string | number>
  >(new Set());
  const [excludedIds, setExcludedIds] = useState<Set<string | number>>(
    new Set()
  );

  // Filter state — when defaultFilterConditions is provided, derive the
  // initial filter_groups from them (keeps a single source of truth) and
  // skip the `defaultFilters.filter_groups` field. Otherwise fall back to
  // the legacy path. A mount-time URL snapshot (initialUrlState) overrides
  // both: its filter set is the complete user set, and its sort/search seed
  // the base params — mirroring exactly what handleApplyAdvancedFilters /
  // handleSortChange / handleSearch would produce had the user applied the
  // same state interactively.
  const [filters, setFilters] = useState<CrmObjectFilters>(() => {
    const base: CrmObjectFilters = {
      search: '',
      page: 1,
      page_size: 20,
      ...defaultFilters,
    };
    if (initialUrlState?.search != null) {
      base.search = initialUrlState.search;
    }
    if (initialUrlState?.sortDescriptor) {
      base.sort = initialUrlState.sortDescriptor.column as string;
      base.order =
        initialUrlState.sortDescriptor.direction === 'ascending'
          ? 'asc'
          : 'desc';
    }
    const urlGroups = initialUrlState?.filterGroups;
    if (urlGroups != null) {
      // URL is the complete user set. In visible-defaults mode the user owns
      // the whole set (an empty array means the default chips were cleared);
      // in the legacy mode, forced defaults must still merge — same rule as
      // handleApplyAdvancedFilters.
      const mergedFilterGroups =
        (defaultFilterConditions?.length ?? 0) > 0
          ? urlGroups
          : mergeWithDefaultFilters(urlGroups, defaultFilters.filter_groups);
      if (mergedFilterGroups.length > 0) {
        return { ...base, filter_groups: mergedFilterGroups };
      }
      const { filter_groups: _omitted, ...baseWithoutGroups } = base;
      return baseWithoutGroups;
    }
    if (defaultFilterConditions && defaultFilterConditions.length > 0) {
      const { filter_groups } = convertConditionGroupsToFilterGroups([
        { id: 'group-0', conditions: defaultFilterConditions },
      ]);
      return { ...base, filter_groups };
    }
    return base;
  });

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Advanced filter state (group-native, authoritative) — seed from the URL
  // snapshot when present (the URL carries the complete user set — including
  // `[]` for "default chips cleared"), otherwise from defaultFilterConditions
  // so the chips appear on first paint. We don't seed from
  // defaultFilters.filter_groups because those are intentionally hidden/forced.
  const seedConditionGroups = (): FilterConditionGroup[] => {
    if (initialUrlState?.filterGroups != null) {
      return initialUrlState.filterGroups.length > 0
        ? filterGroupsToConditionGroups(initialUrlState.filterGroups)
        : EMPTY_FILTER_CONDITION_GROUPS;
    }
    return defaultFilterConditions?.length
      ? [{ id: 'group-0', conditions: defaultFilterConditions }]
      : EMPTY_FILTER_CONDITION_GROUPS;
  };
  const [filterConditionGroups, setFilterConditionGroups] =
    useState<FilterConditionGroup[]>(seedConditionGroups);
  const [appliedFilterConditionGroups, setAppliedFilterConditionGroups] =
    useState<FilterConditionGroup[]>(seedConditionGroups);

  // Derived flat conditions for back-compat consumers.
  const filterConditions = useMemo(
    () => filterConditionGroups.flatMap(g => g.conditions),
    [filterConditionGroups]
  );
  const appliedFilterConditions = useMemo(
    () => appliedFilterConditionGroups.flatMap(g => g.conditions),
    [appliedFilterConditionGroups]
  );

  // Back-compat shim: wraps flat conditions into a single group.
  const setFilterConditions = useCallback((conditions: FilterCondition[]) => {
    setFilterConditionGroups([{ id: 'group-0', conditions }]);
  }, []);

  // Ref for diffing selection changes
  const previousSelectionRef = useRef<Set<string | number>>(new Set());

  // Sort state - initialize from defaultFilters if provided
  // Stabilize: prevents infinite re-renders from unstable conditional expression identity
  const stableInitialSortDescriptor = useMemo<SortDescriptor | null>(() => {
    return defaultFilters.sort
      ? {
          column: defaultFilters.sort,
          direction:
            defaultFilters.order === 'asc' ? 'ascending' : 'descending',
        }
      : null;
  }, [defaultFilters.sort, defaultFilters.order]);

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor | null>(
    () => initialUrlState?.sortDescriptor ?? stableInitialSortDescriptor
  );

  // Handler for search
  const handleSearch = useCallback((query: string) => {
    setFilters(prev => ({
      ...prev,
      search: query,
      page: 1, // Reset to first page when searching
    }));
  }, []);

  // Handler for sort change (react-aria-components)
  const handleSortChange = useCallback((descriptor: SortDescriptor | null) => {
    setSortDescriptor(descriptor);
    if (descriptor) {
      setFilters(prev => ({
        ...prev,
        sort: descriptor.column as string,
        order: descriptor.direction === 'ascending' ? 'asc' : 'desc',
        page: 1,
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        sort: undefined,
        order: undefined,
        page: 1,
      }));
    }
  }, []);

  // Handler for pagination
  const handlePageChange = useCallback((page: number) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
  }, []);

  // Handler for page size change
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setFilters(prev => ({
      ...prev,
      page_size: pageSize,
      page: 1, // Reset to first page when changing page size
    }));
    // Clear selection and exclusions when page size changes
    setSelectedKeys(new Set());
    setExcludedIds(new Set());
  }, []);

  // Handler for filter changes (e.g., stage filter for deals)
  const handleFilterChange = useCallback(
    (key: string, value: string | undefined) => {
      setFilters(prev => {
        // Always reset to first page when filtering
        if (value === undefined) {
          const { [key]: _removed, ...rest } = prev;
          return { ...rest, page: 1 };
        }
        return { ...prev, [key]: value, page: 1 };
      });
    },
    []
  );

  // Handler for applying advanced filters
  // Converts filter condition groups to filter_groups format for POST /query endpoint
  // Preserves defaultFilters.filter_groups when user applies their filters,
  // EXCEPT when the page opted into the visible-defaults flow via
  // `defaultFilterConditions` — in that case the user's conditions are the
  // single source of truth, so removing a chip actually removes the filter.
  const handleApplyAdvancedFilters = useCallback(
    (groups?: FilterConditionGroup[]) => {
      const groupsToApply = groups ?? filterConditionGroups;

      // Snapshot what's now applied so UI that reflects "what's affecting the
      // result set" (badge count, empty state) stays stable while the user
      // edits unapplied conditions in the slideout.
      setAppliedFilterConditionGroups(groupsToApply);

      // Convert to filter_groups format for backend
      const filterGroupsParam =
        convertConditionGroupsToFilterGroups(groupsToApply);

      setFilters(prev => {
        // Keep only base params (search, page, page_size, sort, order)
        const baseParams: CrmObjectFilters = {
          search: prev.search,
          ...(prev.sort && { sort: prev.sort }),
          ...(prev.order && { order: prev.order }),
          page: 1, // Reset to first page
          page_size: prev.page_size,
        };

        // Merge user's filter_groups with default filter_groups — unless the
        // page opted into visible-defaults, in which case the user owns the
        // full filter set. Use `length > 0` (not a truthy check) so an empty
        // `defaultFilterConditions` array doesn't suppress legacy
        // `defaultFilters.filter_groups` (e.g. always-on permission filters).
        const mergedFilterGroups =
          (defaultFilterConditions?.length ?? 0) > 0
            ? filterGroupsParam.filter_groups
            : mergeWithDefaultFilters(
                filterGroupsParam.filter_groups,
                defaultFilters.filter_groups
              );

        if (mergedFilterGroups.length > 0) {
          return {
            ...baseParams,
            filter_groups: mergedFilterGroups,
          };
        }

        // No filter_groups to apply - return base params
        return baseParams;
      });
    },
    [
      filterConditionGroups,
      defaultFilters.filter_groups,
      defaultFilterConditions,
    ]
  );

  // Handler for selection changes with exclusion tracking for 'all' mode
  const handleSelectionChange = useCallback(
    (newSelection: Selection) => {
      const isAllNew = newSelection === 'all';
      const newKeys: Set<string | number> = isAllNew
        ? new Set()
        : new Set([...newSelection] as (string | number)[]);

      const currentIds = options?.currentPageIds ?? [];

      if (selectedKeys === 'all' && !isAllNew) {
        // User deselected or re-selected item(s) while in 'all' mode.
        // Newly deselected: on current page, not in newKeys, not already excluded.
        const deselectedIds = currentIds.filter(
          id => !newKeys.has(id) && !excludedIds.has(id)
        );
        // Re-selected: on current page, now in newKeys, and previously excluded.
        const reselectedIds = currentIds.filter(
          id => newKeys.has(id) && excludedIds.has(id)
        );

        const updatedExclusions = new Set(
          [...excludedIds, ...deselectedIds].filter(
            id => !reselectedIds.includes(id)
          )
        );

        if (updatedExclusions.size > MAX_EXCLUSIONS) {
          options?.onExclusionLimitReached?.();
          return;
        }

        setSelectedKeys('all');
        setExcludedIds(updatedExclusions);
      } else if (selectedKeys === 'all' && isAllNew && excludedIds.size > 0) {
        // 'all' selection re-asserted while already in all mode with exclusions — clear them
        setExcludedIds(new Set());
      } else {
        // Normal selection change - not in all mode
        setSelectedKeys(isAllNew ? 'all' : newKeys);
        setExcludedIds(new Set());
      }

      previousSelectionRef.current = newKeys;
    },
    [selectedKeys, excludedIds, options]
  );

  // Handler for resetting filters
  const handleResetFilters = useCallback(() => {
    setFilterConditionGroups(EMPTY_FILTER_CONDITION_GROUPS);
    setAppliedFilterConditionGroups(EMPTY_FILTER_CONDITION_GROUPS);
    setSortDescriptor(stableInitialSortDescriptor);
    setFilters({
      search: '',
      page: 1,
      page_size: 20,
      ...defaultFilters,
    });
  }, [defaultFilters, stableInitialSortDescriptor]);

  return {
    activeTab,
    setActiveTab,
    selectedKeys,
    setSelectedKeys,
    excludedIds,
    setExcludedIds,
    filters,
    setFilters,
    isCreateModalOpen,
    setIsCreateModalOpen,
    showAdvancedFilters,
    setShowAdvancedFilters,
    filterConditionGroups,
    appliedFilterConditionGroups,
    setFilterConditionGroups,
    filterConditions,
    setFilterConditions,
    appliedFilterConditions,
    handleSearch,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleApplyAdvancedFilters,
    handleResetFilters,
    sortDescriptor,
    handleSortChange,
    handleSelectionChange,
  };
}
